import { z } from 'zod';

import { LEGACY_IDENTITY_ID_PATTERN, LEGACY_IDENTITY_ID_SUBSTRING_PATTERN } from './identity-id';
import {
  actionIdSchema,
  experimentKeySchema,
  flagKeySchema,
  outcomeIdSchema,
  screenIdSchema,
  variantKeySchema,
} from './ids';
import { analyticsEnvironmentSchema } from './subject';

// The single owner of the domain-event `source` grammar (`galapagos.{env}.{domain}`,
// ADR-052). `@galapagos/events` consumes this for the envelope, the same direction
// as its existing `outcomeIdSchema` import.
export const eventSourceSchema = z
  .string()
  .regex(/^galapagos\.[a-z0-9-]+\.[a-z0-9-]+$/, 'source must be galapagos.{env}.{domain}');

export const surfaceSchema = z.enum(['mobile', 'web', 'atlas', 'genui', 'agent', 'api']);
export const platformSchema = z.enum(['ios', 'android', 'web', 'server']);
export const outcomeStatusSchema = z.enum([
  'succeeded',
  'failed',
  'error',
  'timeout',
  'cancelled',
  'blocked',
  'created',
  'completed',
  'updated',
  'deleted',
  'restarted',
  // Delivery-dispatch terminal status (notification / push outcome family).
  'sent',
  // Practice-builder terminal statuses: a refusal is a well-formed answer on
  // the never-error tool contract (the failed status stays reserved for the
  // machinery breaking), so the succeeded outcome charts which arm answered.
  'built',
  'refused',
  // Consent-decision terminal statuses: an appended consent row is a completed
  // write whichever way the member decided, so the outcome charts the decision
  // itself rather than folding it into a constant `completed`.
  'granted',
  'declined',
  'revoked',
  // Journey-composer terminal statuses: a composition run converged
  // ('composed') and a per-slot decision landed ('recorded'). Without these the
  // forwarder rejects the whole annotated outcome as invalid and the events
  // silently never reach the product data plane.
  // Care-team pairing statuses. `ended` rather than `deleted`: unassign closes
  // the assignment's period and never removes the row — that is the feature's
  // one irreversible design commitment, so cataloguing it as a deletion would
  // state the opposite of what happened. `promoted` is the primary changing
  // hands, which is neither a creation nor an update of the pairing itself.
  'ended',
  'promoted',
  'composed',
  'recorded',
  // A composed practice's session was minted from a tile press. Registered for
  // the same reason as the pair above: an unregistered status makes the
  // forwarder reject the entire annotated outcome as invalid.
  'launched',
  // An existing live voice session answered a duplicate mint request. Caught by
  // the closed-vocabulary guard the journey trio's silent drop paid for —
  // voice.session.reused had been dark in PostHog since it shipped.
  'reused',
  // A billing trigger fired for a (member, program). Same guard, same class:
  // billing.trigger.fired had been dark in PostHog since it shipped.
  'fired',
  // A composed arrangement was shown on the home surface. Same guard, same
  // class as the journey trio: unregistered here, the forwarder drops the event.
  'presented',
  // A member opened a delivered notification. Registered for the same reason as
  // every status above it: the forwarder validates the annotated outcome
  // against this closed vocabulary and drops the whole event on a miss, so an
  // unregistered status ships an event that is silently never charted.
  'opened',
  // An app session began — the member's first meaningful interaction inside a
  // ready member scope. A session is neither created nor recorded; it starts.
  // Registered for the same reason as the statuses above: an unregistered
  // status makes the forwarder reject the entire annotated outcome as invalid.
  'started',
  // A write was about to be sent. Registered for the same reason as the statuses
  // above: the closed vocabulary rejects an unregistered status, and an attempt
  // with no outcome of its own is what makes a save that never reached the
  // server visible as a gap between attempts and settled outcomes.
  'attempted',
]);
// How a viewer arrived at a screen. Closed on purpose: an open vocabulary here
// turns the entry-point dimension into free text, and the charts that matter
// ("did this screen get opened from a notification") stop being answerable.
export const entrySourceSchema = z.enum(['organic', 'notification', 'deeplink']);
export type EntrySource = z.infer<typeof entrySourceSchema>;

export const outcomeSourceSchema = z.enum([
  'api',
  'client',
  'domain-event',
  'genui-host',
  'agent-host',
]);
export const showOptionsTerminalStatusSchema = z.enum(['selected', 'timed_out']);
export const evaluationSourceSchema = z.enum(['server', 'snapshot', 'default']);
export const inputMethodSchema = z.enum(['keyboard', 'pointer', 'touch', 'programmatic']);
export type InputMethod = z.infer<typeof inputMethodSchema>;

export const routeTemplateSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(
    /^\/(?:[a-z0-9]+(?:-[a-z0-9]+)*|:[a-z][a-z0-9_-]*)(?:\/(?:[a-z0-9]+(?:-[a-z0-9]+)*|:[a-z][a-z0-9_-]*))*$/,
    'Expected a canonical route template without query values or interpolated identifiers',
  )
  // The member-id shape, not RFC 4122 v4: a version-constrained pattern waves
  // through an imported backend2 id, so a migrated member's id could ride into
  // a route template where a minted member's id is caught.
  .refine(
    (value) => !LEGACY_IDENTITY_ID_SUBSTRING_PATTERN.test(value),
    'Route templates must replace interpolated identifiers with a named parameter',
  );

const stableKebabKeySchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Expected a stable kebab-case key');

const stableTargetAnchorSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z][a-z0-9]*(?:[-_.][a-z0-9]+)*$/, 'Expected a stable target anchor');
const stableVersionSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^\d+(?:\.\d+){1,2}(?:-[a-z0-9.-]+)?$/, 'Expected a stable version');

const safeCorrelationSchema = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[A-Za-z0-9][A-Za-z0-9:_-]*$/, 'Expected a bounded opaque correlation value')
  .refine(
    (value) => !LEGACY_IDENTITY_ID_PATTERN.test(value),
    'Raw UUID values are not valid product analytics correlations',
  );

export const MEMBER_LOCAL_TIMESTAMP_MAX_OFFSET_HOURS = 14 as const;

export const MEMBER_LOCAL_TIMESTAMP_PATTERN =
  '^\\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\\d|3[01])T(?:[01]\\d|2[0-3]):[0-5]\\d:[0-5]\\d(?:\\.\\d{1,3})?(?:Z|\\+(?:(?:0\\d|1[0-3]):[0-5]\\d|14:00)|-(?:00:(?:0[1-9]|[1-5]\\d)|(?:0[1-9]|1[0-3]):[0-5]\\d|14:00))$' as const;

export const memberLocalTimestampSchema = z
  .string()
  .datetime({ offset: true })
  .regex(
    new RegExp(MEMBER_LOCAL_TIMESTAMP_PATTERN),
    'Expected an offset-bearing ISO timestamp with a valid offset',
  );

export const safeProductPropertiesSchema = z
  .object({
    screen_id: screenIdSchema.optional(),
    action_id: actionIdSchema.optional(),
    outcome_id: outcomeIdSchema.optional(),
    plan_persistence_status: z.enum(['saved', 'captured']).optional(),
    surface: surfaceSchema.optional(),
    platform: platformSchema.optional(),
    route_template: routeTemplateSchema.optional(),
    element_key: stableKebabKeySchema.optional(),
    tool_name: stableKebabKeySchema.optional(),
    input_method: inputMethodSchema.optional(),
    status: outcomeStatusSchema.optional(),
    source: outcomeSourceSchema.optional(),
    terminal_status: showOptionsTerminalStatusSchema.optional(),
    flag_key: flagKeySchema.optional(),
    experiment_key: experimentKeySchema.optional(),
    variant_key: variantKeySchema.optional(),
    evaluation_source: evaluationSourceSchema.optional(),
    used_default: z.boolean().optional(),
    target_scope: stableKebabKeySchema.optional(),
    metric_key: outcomeIdSchema.optional(),
    environment: analyticsEnvironmentSchema.optional(),
    contract_version: z
      .string()
      .regex(/^\d+\.\d+\.\d+$/)
      .optional(),
    result: z.enum(['accepted', 'rejected', 'succeeded', 'failed']).optional(),
    error_code: stableKebabKeySchema.optional(),
    correlation_id: safeCorrelationSchema.optional(),
    member_local_timestamp: memberLocalTimestampSchema.optional(),
    duration_ms: z.number().finite().nonnegative().max(86_400_000).optional(),
    count: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
    ok: z.boolean().optional(),
  })
  .passthrough();

export type SafeProductProperties = z.infer<typeof safeProductPropertiesSchema>;

export const CARE_TEAM_VIEW_SOURCES = [
  'derived',
  'unassigned',
  'provisional',
  'fallback',
  'pending',
] as const;
export const careTeamViewSourceSchema = z.enum(CARE_TEAM_VIEW_SOURCES);
export type CareTeamViewSource = z.infer<typeof careTeamViewSourceSchema>;

export const viewPropertiesSchema = z
  .object({
    screen_id: screenIdSchema,
    surface: surfaceSchema,
    platform: platformSchema,
    route_template: routeTemplateSchema,
    environment: safeProductPropertiesSchema.shape.environment,
    contract_version: safeProductPropertiesSchema.shape.contract_version,
    member_local_timestamp: memberLocalTimestampSchema.optional(),
    // How the viewer arrived at this screen. Deliberately NOT `source`: that
    // key is `outcomeSourceSchema`, the emitter-provenance enum (api | client |
    // …), and means something else entirely — overloading it would make
    // "which code emitted this" and "how did the member get here"
    // indistinguishable in every chart that reads it.
    entry_source: entrySourceSchema.optional(),
    // Which care team a care-team surface rendered: a confirmed primary
    // (`derived`), the unassigned state, a primary the chat resolved that the
    // presentation still shows as unassigned (`provisional`), the fallback
    // identity, or nothing yet (`pending`).
    care_team_source: careTeamViewSourceSchema.optional(),
  })
  .passthrough();

export const actionPropertiesSchema = z
  .object({
    screen_id: screenIdSchema,
    action_id: actionIdSchema,
    surface: surfaceSchema,
    platform: platformSchema,
    element_key: stableKebabKeySchema.optional(),
    tool_name: stableKebabKeySchema.optional(),
    input_method: inputMethodSchema.optional(),
    flag_key: flagKeySchema.optional(),
    experiment_key: experimentKeySchema.optional(),
    variant_key: variantKeySchema.optional(),
    used_default: z.boolean().optional(),
    duration_ms: safeProductPropertiesSchema.shape.duration_ms,
  })
  .passthrough();

export const outcomePropertiesSchema = z
  .object({
    outcome_id: outcomeIdSchema,
    plan_persistence_status: safeProductPropertiesSchema.shape.plan_persistence_status,
    status: outcomeStatusSchema,
    source: outcomeSourceSchema,
    flag_key: flagKeySchema.optional(),
    experiment_key: experimentKeySchema.optional(),
    variant_key: variantKeySchema.optional(),
    correlation_id: safeCorrelationSchema.optional(),
    error_code: stableKebabKeySchema.optional(),
    duration_ms: safeProductPropertiesSchema.shape.duration_ms,
    environment: safeProductPropertiesSchema.shape.environment,
    event_source: eventSourceSchema.optional(),
    terminal_status: showOptionsTerminalStatusSchema.optional(),
  })
  .passthrough();

/** Domain-event annotations may lift only these already-validated outcome fields. */
export const FORWARDABLE_OUTCOME_PROPERTY_KEYS = [
  'status',
  'flag_key',
  'experiment_key',
  'variant_key',
  'correlation_id',
  'error_code',
  'duration_ms',
] as const satisfies readonly (keyof z.infer<typeof outcomePropertiesSchema>)[];

export const featureFlagEvaluatedPropertiesSchema = z
  .object({
    flag_key: flagKeySchema,
    variant_key: variantKeySchema,
    evaluation_source: evaluationSourceSchema,
    used_default: z.boolean(),
  })
  .passthrough();

/**
 * Manifest target scopes are snake_case identifiers, but the exposure event's
 * `target_scope` property only admits kebab-case — this converter is the ONE
 * owner of that mapping. The emitter and any readout query joining on the
 * emitted property must both consume it so the join key can never drift.
 */
export function experimentExposureTargetScope(scope: string): string {
  return scope.replace(/_/g, '-');
}

export const experimentAssignedPropertiesSchema = z
  .object({
    experiment_key: experimentKeySchema,
    flag_key: flagKeySchema,
    variant_key: variantKeySchema,
    assignment_source: z.enum(['server', 'qa-override']).optional(),
    member_local_timestamp: memberLocalTimestampSchema.optional(),
  })
  .passthrough();

export const experimentExposedPropertiesSchema = experimentAssignedPropertiesSchema.extend({
  target_scope: stableKebabKeySchema,
  artifact_version: stableVersionSchema.optional(),
  agent_definition_id: stableTargetAnchorSchema.optional(),
  agent_definition_version: stableVersionSchema.optional(),
  agent_screen_id: stableTargetAnchorSchema.optional(),
  agent_screen_version: stableVersionSchema.optional(),
  agent_screen_element_id: stableTargetAnchorSchema.optional(),
  first_message_pool: stableTargetAnchorSchema.optional(),
  flow_id: stableTargetAnchorSchema.optional(),
  flow_version: stableVersionSchema.optional(),
  step_key: stableTargetAnchorSchema.optional(),
  screen_id: screenIdSchema.optional(),
  element_key: stableTargetAnchorSchema.optional(),
  surface_id: stableTargetAnchorSchema.optional(),
  schema_version: stableVersionSchema.optional(),
});

export const experimentOutcomePropertiesSchema = experimentAssignedPropertiesSchema.extend({
  metric_key: outcomeIdSchema,
  status: outcomeStatusSchema,
});

export type ViewProperties = z.infer<typeof viewPropertiesSchema>;
export type ActionProperties = z.infer<typeof actionPropertiesSchema>;
export type OutcomeProperties = z.infer<typeof outcomePropertiesSchema>;
export type FeatureFlagEvaluatedProperties = z.infer<typeof featureFlagEvaluatedPropertiesSchema>;
export type ExperimentAssignedProperties = z.infer<typeof experimentAssignedPropertiesSchema>;
export type ExperimentExposedProperties = z.infer<typeof experimentExposedPropertiesSchema>;
export type ExperimentOutcomeProperties = z.infer<typeof experimentOutcomePropertiesSchema>;
export type ViewPropertiesInput = z.input<typeof viewPropertiesSchema>;
export type ActionPropertiesInput = z.input<typeof actionPropertiesSchema>;
export type OutcomePropertiesInput = z.input<typeof outcomePropertiesSchema>;
export type FeatureFlagEvaluatedPropertiesInput = z.input<
  typeof featureFlagEvaluatedPropertiesSchema
>;
export type ExperimentAssignedPropertiesInput = z.input<typeof experimentAssignedPropertiesSchema>;
export type ExperimentExposedPropertiesInput = z.input<typeof experimentExposedPropertiesSchema>;
export type ExperimentOutcomePropertiesInput = z.input<typeof experimentOutcomePropertiesSchema>;
